import { ContextProvider } from '@lit/context'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import componentStyle from '../common/styles/Component.css'

import { navListContext } from './nav-context.js'
import style from './NavGroup.css'
import '../icon/Icon.js'

// Mirrors the layout's wide-viewport breakpoint. Used to drive the
// `data-rail-collapsed` attribute below — Safari doesn't support
// `:host-context()`, so we propagate the layout's rail state via an
// observed attribute on the host instead.
const railMediaQuery
  = typeof matchMedia !== 'undefined'
    ? matchMedia('(min-width: 768px)')
    : ({
        matches: false,
        addEventListener() {
          /* noop */
        },
        removeEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)

/**
 * Navigation group includes all the actions or items in a single
 * group and is used for grouping items into related categories.
 *
 * @category navigation
 * @status ready
 * @slot - The default slot used for the nav items.
 *
 * @fires {NordEvent} toggle - Dispatched whenever a nav item's state changes between open and closed.
 * @fires {NordEvent} activate - Dispatched whenever a nav item has been marked as active
 *
 * @cssprop [--n-nav-group-heading-display=block] - Controls the `display` of the group's heading. Set to `none` to hide it — useful for a `slot="subnav"` group whose heading repeats the parent item's label inline; the heading is still shown in the rail flyout where it titles the projected list.
 *
 * @parent Navigation
 */
@customElement('nord-nav-group')
export default class NavGroup extends LitElement {
  static styles = [componentStyle, style]

  // Exposes `role="list"` to slotted nav-items so they know to emit
  // `role="listitem"`. See nav-context.ts.
  private listProvider = new ContextProvider(this, { context: navListContext, initialValue: true })

  private layoutObserver?: MutationObserver
  private observedLayout?: HTMLElement | null

  /**
   * Heading and accessible label for the nav group
   */
  @property({ reflect: true }) heading?: string

  connectedCallback() {
    super.connectedCallback()
    this.startObservingLayout()
    railMediaQuery.addEventListener('change', this.handleViewportChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.stopObservingLayout()
    railMediaQuery.removeEventListener('change', this.handleViewportChange)
  }

  private handleViewportChange = () => this.updateRailState()

  private startObservingLayout() {
    this.observedLayout = this.closest<HTMLElement>('nord-layout')
    if (!this.observedLayout) {
      return
    }
    this.updateRailState()
    this.layoutObserver = new MutationObserver(() => this.updateRailState())
    this.layoutObserver.observe(this.observedLayout, {
      attributes: true,
      attributeFilter: ['nav-open', 'collapse-mode'],
    })
  }

  private stopObservingLayout() {
    this.layoutObserver?.disconnect()
    this.layoutObserver = undefined
    this.observedLayout = undefined
    this.toggleAttribute('data-rail-collapsed', false)
  }

  private updateRailState() {
    if (!this.observedLayout) {
      return
    }
    const next = railMediaQuery.matches
      && this.observedLayout.getAttribute('collapse-mode') === 'rail'
      && !this.observedLayout.hasAttribute('nav-open')
    this.toggleAttribute('data-rail-collapsed', next)
  }

  render() {
    return html`
      ${this.heading ? html`<p id="heading" aria-hidden="true" class="n-heading">${this.heading}</p>` : nothing}
      <div role="list" aria-labelledby=${this.heading ? 'heading' : nothing}>
        <slot></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-nav-group': NavGroup
  }
}
